/**
 * The 叠加关系 matrix, derived from each program's stored `mutuallyExclusive` lists.
 *
 * This mirrors `RecommendationEngineImpl.isMutuallyExclusive` exactly, so the matrix
 * shows what the engine does today:
 *   - per region (`global`, `china`), two programs are exclusive when EITHER lists the
 *     other's name (the engine checks both directions);
 *   - names are matched exactly (case-sensitive), so a misspelt entry excludes nothing
 *     (and is drawn that way: the matrix shows the engine's behaviour, not the intent).
 * Relations the engine hardcodes elsewhere (e.g. the year rules in `isLegal`) are not
 * stored data and are deliberately not shown.
 */
import type { InvestmentProgramData } from '../client/CpaClient';

export const REGIONS = ['global', 'china'] as const;
export type Region = (typeof REGIONS)[number];

/**
 * `stackable`   — stackable in both regions
 * `exclusive`   — exclusive in both regions
 * `china_only`  — stackable only in China (exclusive in global)
 * `global_only` — stackable only in global (exclusive in China)
 */
export type Relation = 'self' | 'stackable' | 'exclusive' | 'china_only' | 'global_only';

/** Which side's list made a pair exclusive in a region (both may). */
export interface ExclusionSource {
  region: Region;
  listedBy: string[];
}

export interface StackingCell {
  relation: Relation;
  sources: ExclusionSource[];
}

export interface StackingMatrix {
  programs: InvestmentProgramData[];
  cell: (row: InvestmentProgramData, col: InvestmentProgramData) => StackingCell;
}

const listOf = (p: InvestmentProgramData, region: string) => p.mutuallyExclusive?.[region] ?? [];

function relationOf(globalExclusive: boolean, chinaExclusive: boolean): Relation {
  if (globalExclusive && chinaExclusive) return 'exclusive';
  if (globalExclusive) return 'china_only';
  if (chinaExclusive) return 'global_only';
  return 'stackable';
}

export function buildStackingMatrix(programs: InvestmentProgramData[]): StackingMatrix {
  const ordered = [...programs].sort(
    (a, b) =>
      (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER) ||
      (a.name ?? '').localeCompare(b.name ?? ''),
  );
  const cell = (row: InvestmentProgramData, col: InvestmentProgramData): StackingCell => {
    if (row.programId === col.programId) return { relation: 'self', sources: [] };
    const sources = REGIONS.map((region) => ({
      region,
      listedBy: [
        ...(col.name !== undefined && listOf(row, region).includes(col.name) ? [row.name ?? ''] : []),
        ...(row.name !== undefined && listOf(col, region).includes(row.name) ? [col.name ?? ''] : []),
      ],
    })).filter((s) => s.listedBy.length > 0);
    const exclusiveIn = (region: Region) => sources.some((s) => s.region === region);
    return { relation: relationOf(exclusiveIn('global'), exclusiveIn('china')), sources };
  };

  return { programs: ordered, cell };
}

/** How many unordered pairs have each relation (the diagonal is not counted). */
export function countRelations(matrix: StackingMatrix): Record<Exclude<Relation, 'self'>, number> {
  const counts = { stackable: 0, exclusive: 0, china_only: 0, global_only: 0 };
  const { programs, cell } = matrix;
  for (let i = 0; i < programs.length; i++) {
    for (let j = i + 1; j < programs.length; j++) {
      counts[cell(programs[i], programs[j]).relation as keyof typeof counts]++;
    }
  }
  return counts;
}
